import { useContext } from 'react'
import { useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { Waypoint } from 'react-waypoint'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'

// Components
import { Button, Panel, Format, Text } from '../..'
import QuestionSingle from '../Questions/Single'
import OverlayPanel from '../../shared/Routing/OverlayPanel'
import { ReverseMortgagePanel } from './ReverseMortgagePanel'

// Actions
import { trackReverseMortgageView } from '../../../actions/home'

// Utils
import { LearnMore } from '../../../constants/trackingEvents'
import history from '../../../history'
import { useAmplitudeDebounceSetters } from '../../../hooks/tracking'

// Constants
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import { QUESTIONS } from '../../../constants/messages'
import { REVERSE_MORTGAGE_TOPIC_KEY } from '../../../constants/topicKeys'

// Styles
import styles from './ReverseMortgagesModule.module.scss'

const propTypes = {
  equityPercent: PropTypes.number,
  showInfo: PropTypes.bool,
}

const ReverseMortgagesModule = ({ equityPercent, showInfo }) => {
  const dispatch = useDispatch()

  const { ampli } = useContext(AmplitudeContext)

  const handleReverseMortgagePanelOpen = () => {
    dispatch(trackReverseMortgageView())
    history.push(OVERLAY_HELPERS.reverseMortgage.buildPath())
  }
  const intl = useIntl()

  const [trackModuleViewed] = useAmplitudeDebounceSetters(() =>
    ampli?.moduleViewed({
      module_name: AmplitudeEventConstants.REVERSE_MORTGAGE,
    })
  )

  return (
    <Panel data-qa='ReverseMortgagesCard' className={styles.card}>
      <Panel.Heading className={styles.heading}>
        <FormattedMessage
          id='Homeowner.reverseMortgages.heading'
          defaultMessage='Retirement on the horizon?'
        />
        <br />
        <FormattedMessage
          id='Homeowner.reverseMortgages.subHeading'
          defaultMessage='Your estimated home equity is:'
        />
      </Panel.Heading>

      <Text
        size='xxxl'
        weight='light'
        variant='success'
        className={styles.equity}
      >
        <Format.Percentage value={equityPercent} format='0.0' />
      </Text>
      <Waypoint onEnter={trackModuleViewed} bottomOffset='50%'>
        <div />
      </Waypoint>
      <Text
        variant='neutralCool'
        variantCount='700'
        className={styles.description}
      >
        <FormattedMessage
          id='Homeowner.reverseMortgages.description'
          defaultMessage='This means you could possibly get a reverse mortgage, which might help you better balance home equity and income during retirement'
        />
      </Text>

      <Button
        dataQa='reverse-mortgages-details-button'
        className={styles.button}
        onClick={handleReverseMortgagePanelOpen}
        track={LearnMore}
        trackEnabled={!showInfo}
        trackOptions={{ eventLabel: 'ReverseMortgage' }}
      >
        <FormattedMessage
          id='Homeowner.reverseMortgages.expandButton'
          defaultMessage='Learn more about this'
        />
      </Button>
      <Panel.Footer>
        <QuestionSingle
          topic={QUESTIONS.reverseMortgagesTopic}
          topicKey={REVERSE_MORTGAGE_TOPIC_KEY}
          question={QUESTIONS.reverseMortgagesQuestion}
        />
      </Panel.Footer>

      <OverlayPanel
        back={OVERLAY_HELPERS.reverseMortgage.getOrigin()}
        path={OVERLAY_HELPERS.reverseMortgage.buildPath()}
        title={OVERLAY_HELPERS.getTitle('reverseMortgage', intl)}
        component={ReverseMortgagePanel}
      />
    </Panel>
  )
}

ReverseMortgagesModule.propTypes = propTypes

export default ReverseMortgagesModule
